
import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner } from '../../../../domain/types';

interface HeroCarouselProps {
    slides: Banner[];
}

export const HeroCarousel = memo(({ slides = [] }: HeroCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const navigate = useNavigate();
    const DURATION = 5000;

    useEffect(() => {
        if (!slides || slides.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % (slides?.length || 1));
        }, DURATION);
        return () => clearInterval(timer);
    }, [slides?.length, DURATION]);

    const handleBannerClick = (e: React.MouseEvent) => {
        // Solo navega si se toca específicamente el "botón" (la acción)
        const isAction = (e.target as HTMLElement).closest('.slide-action');
        if (isAction) {
            navigate(slides[currentIndex].linkTo);
        } else {
            // Tocar el fondo o el título pasa a la siguiente imagen
            setCurrentIndex((prev) => (prev + 1) % slides.length);
        }
    };

    if (!slides || slides.length === 0) {
        return (
            <div className="w-full h-56 sm:h-72 bg-black/5 dark:bg-white/5 rounded-3xl flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>
                <span className="text-gray-400 text-xs font-bold uppercase tracking-widest opacity-20">AllTuc Premium</span>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col items-stretch justify-start rounded-3xl overflow-hidden shadow-lg shadow-black/5 dark:shadow-black/20 bg-surface-light dark:bg-surface-dark relative group h-56 sm:h-72 cursor-pointer" onClick={handleBannerClick}>
            {slides.map((slide, index) => (
                <div key={slide.id} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                    <div className="w-full h-full bg-center bg-no-repeat bg-cover absolute inset-0 transition-transform duration-[5000ms] ease-out scale-100 group-hover:scale-105" style={{backgroundImage: `url("${slide.imageUrl}")`}}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-6">
                        <div className="flex flex-col text-white animate-slide-up">
                            <span className="font-bold text-2xl mb-1 leading-tight drop-shadow-md">{slide.title}</span>
                            {slide.subtitle && (
                                <span className="text-white/90 text-sm font-medium bg-white/20 backdrop-blur-md self-start px-3 py-1 rounded-full border border-white/10 slide-action hover:bg-white/30 transition-colors">
                                    {slide.subtitle}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            
            {/* Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
                <div 
                    key={currentIndex} 
                    className="h-full bg-primary animate-progress" 
                    style={{ animationDuration: `${DURATION}ms` }}
                ></div>
            </div>

            <div className="absolute bottom-4 right-4 z-20 flex gap-2">
                {slides.map((_, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }} className={`h-1.5 rounded-full transition-all duration-500 ${i === currentIndex ? 'bg-white w-6' : 'bg-white/40 w-2'}`} aria-label={`Slide ${i}`}></button>
                ))}
            </div>
        </div>
    );
});
HeroCarousel.displayName = 'HeroCarousel';
